import { Component, ElementRef, EventEmitter, HostBinding, HostListener, OnDestroy, OnInit, Output, ViewChild, AfterViewInit } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { MessageSeverity } from '../../../enums/message-severity';
import { SettingsService } from '../../../services/settings/settings.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { ActionButton } from '../../../models/app/action-button';
import { ChipSelectorComponent } from '../../fragments/chip-selector/chip-selector.component';
import { AvatarPrimitive } from '../../../models/app/avatar-primitive';
import { OrganizationalUnitService } from '../../../services/organizational-units/organizational-unit.service';
import { UserSession } from '../../../interfaces/user-session';
import { PersonSearchRequest } from '../../../models/net/request/person-search-request';
import { PersonSearchResult } from '../../../models/net/result/person-search-result';
import { PersonEnvelope } from '../../../models/net/envelope/person-envelope';
import { PersonService } from '../../../services/persons/person.service';
import { ReadResult } from '../../../models/net/result/crud/read-result';
import { OrganizationalUnit } from '../../../models/data/app-core/organizational-unit';
import { OrganizationalUnitEnvelope } from '../../../models/net/envelope/organizational-unit-envelope';
import { OrganizationalUnitPerson } from '../../../models/data/app-core/organizational-unit-person';
import { Person } from '../../../models/data/app-core/person';
import { TelemetorComponent } from '../../base/telemetor/telemetor.component';
import { ScreenSize } from '../../../enums/screen-size';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['../../../app.component.css', './home.component.css']
})

export class HomeComponent extends TelemetorComponent implements OnInit, AfterViewInit, OnDestroy {

    public subdomain: string = "";

    public dataSource: any = null;
    public title: string = "Session:";
    public subtitle: string = "You are logged in with user credentials {User.UID} with roles {roles.map((role) => role.UID)}";
    public availableOrganizationalUnits: AvatarPrimitive[] = [];
    public selectedOrganizationalUnitIds: number[] = [];
    public currentPerson: Person | undefined;

    showDropdown = false;

    @HostBinding("class.component-flex-vertical-fill") addClass = true;

    @Output() screenSizeChange = new EventEmitter<ScreenSize>();
    @Output() componentReady = new EventEmitter<string>();

    public avatarUri: string = "";

    @ViewChild(MatSort) sort = new MatSort();
    @ViewChild(MatPaginator) paginator: any;
    @ViewChild("organizationalUnitChipSelector") organizationalUnitChipSelector!: ChipSelectorComponent;

    private destroy$ = new Subject<void>();

    // Slideshow related
    @ViewChild('slideshowContainer', { read: ElementRef }) slideshowContainer!: ElementRef<HTMLElement>;
    private slidesElement!: HTMLElement | null;
    private dotsContainer!: HTMLElement | null;
    private slideImgs: HTMLImageElement[] = [];
    public activeIndex: number = 0;

    public constructor(public appService: AppService,
                       private settingsService: SettingsService,
                       private personService: PersonService, 
                       private route: ActivatedRoute) {
        super();
    }

    public override ngOnInit(): void {

        const hostname: string = window.location.hostname;
        const parts = hostname.split('.');

        const parameters = this.route.params;

        if (parameters != null) {

            parameters.subscribe((p) => {

                const themeName: string = p["themename"];

                if (themeName) {

                    if ((themeName.toLowerCase() == "dark" || themeName.toLowerCase() == "night") && this.appService.themeService.currentThemeName != "dark") {

                        this.appService.toggleTheme("dark");

                    } else if ((themeName.toLowerCase() == "light" || themeName.toLowerCase() == "day") && this.appService.themeService.currentThemeName != "light") {

                        this.appService.toggleTheme("light");
                    }
                }
            });
        }

        if (parts.length > 1) {

            this.subdomain = parts[0];
        }

        let actionButtons: ActionButton[] = [];

        this.appService.setActionsDataSource(actionButtons);

        this.emitTelemetrigram("load");

        const currentPath = this.route.snapshot.routeConfig?.path;

        if (currentPath === 'subscribe') {

            this.appService.showSubscribeDialog();
        }
    }

    public ngAfterViewInit(): void {

        // initialize slideshow elements
        try {
            this.slidesElement = this.slideshowContainer?.nativeElement.querySelector('.slides') as HTMLElement;
            this.dotsContainer = this.slideshowContainer?.nativeElement.querySelector('.slideshow-dots') as HTMLElement;
            const imgs = this.slideshowContainer?.nativeElement.querySelectorAll('img');
            this.slideImgs = Array.from(imgs) as HTMLImageElement[];

            // Attach handlers to support theme fallback: prefer dark image, but if missing,
            // fall back to light image and apply CSS inversion filter.
            const currentTheme = this.appService.themeService.currentThemeName;
            const altTheme = currentTheme === 'dark' ? 'light' : 'dark';

            this.slideImgs.forEach((img: HTMLImageElement) => {
                // ensure not draggable
                img.draggable = false;

                // on load: if fallback was applied keep inversion (we applied it intentionally),
                // otherwise clear any inline filter
                img.addEventListener('load', () => {
                    if (img.dataset['fallbackApplied'] === 'true') {
                        // keep inversion for fallback images
                        img.style.filter = 'invert(100%)';
                        img.style.webkitFilter = 'invert(100%)';
                    } else {
                        img.style.filter = '';
                        img.style.webkitFilter = '';
                    }
                });

                img.addEventListener('error', () => {
                    try {
                        // don't attempt fallback more than once
                        if (img.dataset['fallbackAttempted'] === 'true') return;
                        img.dataset['fallbackAttempted'] = 'true';

                        const src = img.getAttribute('src') || '';
                        // prefer replacing theme token if present (e.g., _dark -> _light)
                        let fallbackSrc = '';

                        if (currentTheme && src.indexOf(`_${currentTheme}`) !== -1) {
                            fallbackSrc = src.replace(`_${currentTheme}`, `_${altTheme}`);
                        } else {
                            // as a safer fallback, try swapping 'dark'/'light' tokens if present
                            if (src.indexOf('_dark') !== -1) {
                                fallbackSrc = src.replace('_dark', '_light');
                            } else if (src.indexOf('_light') !== -1) {
                                // if we errored on light (unlikely), do nothing
                                fallbackSrc = src;
                            } else {
                                // no recognizable token - try appending _light before extension
                                const m = src.match(/^(.*)(\.[a-zA-Z0-9]+)$/);
                                if (m) {
                                    fallbackSrc = `${m[1]}_light${m[2]}`;
                                }
                            }
                        }

                        if (fallbackSrc && fallbackSrc !== src) {
                            // mark that we applied fallback and visually invert image
                            img.dataset['fallbackApplied'] = 'true';
                            img.style.filter = 'invert(100%)';
                            img.style.webkitFilter = 'invert(100%)';
                            // assign fallback src
                            img.src = fallbackSrc;
                        }
                    } catch (e) {
                        // ignore
                    }
                });
            });

            // ensure first image visible
            this.activeIndex = 0;
            this.updateSlidePosition();

            // render dots based on count
            this.renderDots();

            // update controls (disable prev at start)
            this.updateControls();

            window.addEventListener('resize', this.onWindowResize);
            // Pointer (swipe) support for touch and mouse
            this.initPointerDragHandlers();
        } catch (e) {
            // ignore if slideshow not present
        }

        this.componentReady.emit(this.constructor.name);
    }

    private onWindowResize = (): void => {
        this.updateSlidePosition();
    }

    private renderDots(): void {
        if (!this.dotsContainer) return;
        this.dotsContainer.innerHTML = '';
        this.slideImgs.forEach((img, idx) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'dot';
            btn.setAttribute('aria-label', `Go to slide ${idx + 1}`);
            btn.setAttribute('aria-pressed', idx === this.activeIndex ? 'true' : 'false');
            // inline styles to override any global button styling from libraries
            btn.style.all = 'unset';
            btn.style.display = 'inline-block';
            btn.style.width = idx === this.activeIndex ? '9px' : '6px';
            btn.style.height = idx === this.activeIndex ? '9px' : '6px';
            btn.style.borderRadius = '50%';
            btn.style.background = idx === this.activeIndex ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.45)';
            btn.style.boxShadow = idx === this.activeIndex ? '0 1px 4px rgba(0,0,0,0.35)' : 'inset 0 0 0 1px rgba(0,0,0,0.06)';
            btn.style.cursor = 'pointer';
            btn.style.padding = '0';
            btn.style.margin = '0';
            btn.style.opacity = '0.95';
            btn.addEventListener('click', (ev) => { ev.stopPropagation(); this.showSlide(idx); });
            this.dotsContainer!.appendChild(btn);
        });
    }

    public showSlide(index: number): void {
        if (!this.slidesElement || this.slideImgs.length === 0) return;
        const clamped = Math.max(0, Math.min(index, this.slideImgs.length - 1));
        this.activeIndex = clamped;
        this.updateSlidePosition();
        this.updateDots();
        this.updateControls();
    }

    private updateSlidePosition(): void {
        if (!this.slidesElement) return;
        const offset = -this.activeIndex * this.slidesElement.clientWidth;
        this.slidesElement.style.transform = `translateX(${offset}px)`;
    }

    private updateDots(): void {
        if (!this.dotsContainer) return;
        const buttons = Array.from(this.dotsContainer.querySelectorAll('button')) as HTMLButtonElement[];
        buttons.forEach((b, i) => {
            if (i === this.activeIndex) {
                b.classList.add('active');
                b.setAttribute('aria-pressed', 'true');
                b.style.width = '9px';
                b.style.height = '9px';
                b.style.background = 'rgba(0,0,0,0.95)';
                b.style.boxShadow = '0 1px 4px rgba(0,0,0,0.35)';
            } else {
                b.classList.remove('active');
                b.setAttribute('aria-pressed', 'false');
                b.style.width = '6px';
                b.style.height = '6px';
                b.style.background = 'rgba(0,0,0,0.45)';
                b.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.06)';
            }
        });
    }

    private updateControls(): void {
        // disable prev if at start; disable next if at end
        const prevBtn = this.slideshowContainer?.nativeElement.querySelector('.prev') as HTMLButtonElement | null;
        const nextBtn = this.slideshowContainer?.nativeElement.querySelector('.next') as HTMLButtonElement | null;
        if (prevBtn) {
            prevBtn.disabled = this.activeIndex <= 0;
            prevBtn.style.opacity = this.activeIndex <= 0 ? '0.35' : '1';
            prevBtn.style.pointerEvents = this.activeIndex <= 0 ? 'none' : 'auto';
        }
        if (nextBtn) {
            nextBtn.disabled = this.activeIndex >= this.slideImgs.length - 1;
            nextBtn.style.opacity = this.activeIndex >= this.slideImgs.length - 1 ? '0.35' : '1';
            nextBtn.style.pointerEvents = this.activeIndex >= this.slideImgs.length - 1 ? 'none' : 'auto';
        }
    }

    public nextSlide(): void {
        if (this.slideImgs.length === 0) return;
        if (this.activeIndex >= this.slideImgs.length - 1) return; // do not loop
        this.showSlide(this.activeIndex + 1);
    }

    public prevSlide(): void {
        if (this.slideImgs.length === 0) return;
        if (this.activeIndex <= 0) return;
        this.showSlide(this.activeIndex - 1);
    }

    public onSlideshowKey(event: KeyboardEvent): void {
        if (event.key === 'ArrowLeft') {
            this.prevSlide();
            event.preventDefault();
        } else if (event.key === 'ArrowRight') {
            this.nextSlide();
            event.preventDefault();
        }
    }

    public showDialog = async (isShowAppFeed: boolean = false): Promise<void> => {

        this.appService.IsAppBusy = true;

        await this.delay(1800);

        this.appService.IsAppBusy = false;

        let message = "Demonstration to check the functionality of app dialogs";

        if (isShowAppFeed) {

            message += " This is a demo error message (with AppFeed) after showing the loader"

            this.appService.showDialog(MessageSeverity.Error, "Demo", message, "OH CRAP !!", "", true, true);

        } else {

            this.appService.showDialog(MessageSeverity.Success, "Demo", message, "SOUNDS GOOD !", "");
        }
    }

    public getSettings = async (): Promise<void> => {

        this.settingsService.getSettings().subscribe((settings) => {

            if (settings) {

                if (settings instanceof HttpErrorResponse) {

                    this.appService.showDialog(MessageSeverity.Error, "Settings result", "Unable to get settings :(", "DANG !", "", true, true);

                } else {

                    this.appService.showDialog(MessageSeverity.Info, "Settings result", "GotSettings where JSON.stringify(settings) is " + JSON.stringify(settings), "GREAT !", "", true, true);
                }
            }
        });
    }

    public get targetSubdomain(): string {

        var result: string = this.subdomain === "www" ? "mendls" : "www";

        return result;
    }

    public get targetSubdomainName(): string {

        var result: string = this.subdomain === "www" ? "Go to Mendl's Boulangerie / Patisserie" : "Explore Lutz Angeles";

        return result;
    }

    private delay = (ms: number): Promise<void> => {

        let result: Promise<void> = new Promise((resolve) => setTimeout(resolve, ms));

        return result;
    }

    public onCardClick = (): void => {

        this.appService.goToSubdomain("mendls");
    }

    public onMendlsCardClick = (): void => {

        this.appService.navigateTo("show/bakery", "mendls");
    }

    public shareLutzAngeles = (): void => {

        const telemetrigramOperation: string = "share:Lutz Angeles;Home of Mendl\'s";

        this.appService.share("Lutz Angeles", "Home of Mendl's");

        this.emitTelemetrigram(telemetrigramOperation.substring(0, 100));
    }

    public shareMendls = (): void => {

        const telemetrigramOperation: string = "share:Mendl's;Boulangerie-Pâtisserie";

        this.appService.share("Mendl's", "Boulangerie-Pâtisserie");

        this.emitTelemetrigram(telemetrigramOperation.substring(0, 100));
    }

    private initPointerDragHandlers(): void {
        const slideshow = this.slideshowContainer?.nativeElement as HTMLElement;
        const slidesTrack = this.slidesElement as HTMLElement;
        if (!slideshow || !slidesTrack) return;

        let startX = 0;
        let currentX = 0;
        let isDragging = false;
        const threshold = 30; // px required to trigger slide change

        const onPointerDown = (e: PointerEvent) => {
            // ignore if target is a control
            const target = e.target as HTMLElement;
            if (target.closest('.slideshow-nav') || target.closest('.slideshow-dots') || target.tagName === 'BUTTON') return;

            isDragging = true;
            startX = e.clientX;
            // prevent default to avoid unwanted browser gestures (when appropriate)
            try { e.preventDefault(); } catch (err) { }
            try { (slideshow as any).setPointerCapture?.(e.pointerId); } catch (err) { }
            slidesTrack.style.transition = 'none';
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!isDragging) return;
            currentX = e.clientX;
            const delta = currentX - startX;
            slidesTrack.style.transform = `translateX(${
                (-this.activeIndex * slideshow.clientWidth) + delta
            }px)`;
        };

        const onPointerUp = (e: PointerEvent) => {
            if (!isDragging) return;
            isDragging = false;
            try { (slideshow as any).releasePointerCapture?.(e.pointerId); } catch (err) { }
            // restore CSS transition
            slidesTrack.style.transition = 'transform 0.4s ease-in-out';
            const endX = (typeof e.clientX === 'number' && e.clientX !== 0) ? e.clientX : currentX || startX;
            const delta = endX - startX;
            if (delta < -threshold) {
                this.nextSlide();
            } else if (delta > threshold) {
                this.prevSlide();
            } else {
                this.showSlide(this.activeIndex); // snap back
            }
        };

        slideshow.addEventListener('pointerdown', onPointerDown);
        slideshow.addEventListener('pointermove', onPointerMove);
        slideshow.addEventListener('pointerup', onPointerUp);
        slideshow.addEventListener('pointercancel', onPointerUp);
        slideshow.addEventListener('pointerleave', onPointerUp);

        // store handlers so they can be removed
        (this as any)._slideshowPointerHandlers = { onPointerDown, onPointerMove, onPointerUp };
    }

    public ngOnDestroy() {

        this.appService.setActionsDataSource([]);
        this.destroy$.next();
        this.destroy$.complete();

        window.removeEventListener('resize', this.onWindowResize);
        try {
            const slideshow = this.slideshowContainer?.nativeElement as HTMLElement;
            const handlers = (this as any)._slideshowPointerHandlers;
            if (slideshow && handlers) {
                slideshow.removeEventListener('pointerdown', handlers.onPointerDown);
                slideshow.removeEventListener('pointermove', handlers.onPointerMove);
                slideshow.removeEventListener('pointerup', handlers.onPointerUp);
                slideshow.removeEventListener('pointercancel', handlers.onPointerUp);
                slideshow.removeEventListener('pointerleave', handlers.onPointerUp);
            }
        } catch (e) {
            // ignore
        }
    }
}
